import type { RecordFolderAssignment } from '@provision/entity-types';
import { QueryHookOptions, useQuery } from '@provision/graphql-client';
import { useMemo } from 'react';
import { isNullish } from 'remeda';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import {
  RecordFolder,
  RecordFolderArea,
  RecordFolderVisibility,
  useCurrentUserProfile,
  useGroupRules,
  UserGroupRules,
  UserSettingId,
} from 'src/graphql';
import { MAX_RECORD_FOLDERS_LIMIT } from 'src/graphql/entities/record-folder/constants';
import { GET_RECORD_FOLDERS_QUERY } from 'src/graphql/entities/record-folder/record-folder.operations';
import { useRecordFolderAssignments } from 'src/graphql/entities/record-folder-assignment';
import { Records } from 'src/graphql/types';
import { useGlobalStore } from 'src/store';

export interface GetRecordFolder {
  getEnabledRecordFolders: Records<RecordFolder>;
}

interface UseRecordFoldersOptions extends QueryHookOptions<GetRecordFolder> {
  area?: RecordFolderArea;
  partnerId?: number | null;
  assignmentEntityFilter?: string[];
  recordFolderAssignments?: RecordFolderAssignment[];
}

export const useRecordFolders = (options?: UseRecordFoldersOptions) => {
  const { user } = useCurrentUserProfile();
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const partnerId = isNullish(options?.partnerId)
    ? (selectedPartnerId ?? PLATFORM_PARTNER_ID)
    : options?.partnerId;
  const { checkValidity } = useGroupRules();
  const area = options?.area;
  const assignmentEntityFilter =
    options?.assignmentEntityFilter ??
    (area === RecordFolderArea.Actions ? ['Macro'] : ['Conversation', 'CustomView']);
  const providedAssignments = options?.recordFolderAssignments;

  const { data, loading } = useQuery<GetRecordFolder>(GET_RECORD_FOLDERS_QUERY, {
    ...options,
    context: {
      includePlatformLevelData: true,
    },
    variables: {
      partnerId,
      area,
      limit: MAX_RECORD_FOLDERS_LIMIT,
    },
  });

  const { recordFolderAssignments: fetchedAssignments } = useRecordFolderAssignments({
    entityFilter: assignmentEntityFilter,
    skip: Boolean(providedAssignments),
  });
  const recordFolderAssignments = providedAssignments ?? fetchedAssignments;

  const recordFolders = useMemo<RecordFolder[]>(() => {
    const existingUserSettingValue = user?.userSettingValueMap.get(
      UserSettingId.HiddenRecordFolders,
    );

    const hiddenIds = (existingUserSettingValue?.value ?? []) as string[];

    const list = data?.getEnabledRecordFolders?.records ?? [];

    return list
      .filter((folder) => (area ? (folder.area ?? RecordFolderArea.View) === area : true))
      .map((i) => ({
        ...i,
        assignments: recordFolderAssignments.filter(
          (assignment) => assignment.recordFolderId === i.id,
        ),
        visibility: hiddenIds.includes(i.recordFolderId)
          ? RecordFolderVisibility.Hidden
          : RecordFolderVisibility.Shown,
      }))
      .sort((a, b) => {
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data?.getEnabledRecordFolders?.records,
    recordFolderAssignments,
    recordFolderAssignments.length,
    user,
    area,
  ]);

  const canManagePublicFolders = checkValidity([UserGroupRules.AccessToManagePublicFolders]);

  const manageableRecordFolders = useMemo(() => {
    return canManagePublicFolders
      ? recordFolders
      : recordFolders.filter((folder) => folder.isPrivate);
  }, [canManagePublicFolders, recordFolders]);

  return {
    recordFolders,
    manageableRecordFolders,
    isEnabled: true,
    isLoading: loading,
  };
};
